import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import { AppRegistry, ModelRegistry, StoreRegistry } from 'core/registry';
import { Model, App, ArrayApp } from 'core';

import { ThreeColumnLayout } from 'apps/layout';
import { PermissionedModel } from 'apps/permissions/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { TagModel, TagsApp } from 'apps/tag/app';
import { FileModel, FileApp } from 'apps/file/app';
import { conf as tagsConf } from 'apps/tag/config.yaml';
import { conf } from 'apps/project/config.yaml';
import { LicensesInfo } from 'apps/project/license_details';

export class ProjectPhaseModel extends Model {
  static registryName = 'ProjectPhaseModel';
  @observable name = '';
  @observable color = '';
  @observable order = 0;
}

export class ProjectPhaseApp extends App {
  static registryName = 'ProjectPhaseApp';
  static Model = ProjectPhaseModel;
}

export class ProjectModel extends PermissionedModel {
  static registryName = 'ProjectModel';
  static ModelProps = {
    tags: [TagModel],
    image: FileModel,
    creatorProfile: 'ProfileModel',
    parentContent: 'ContentModel',
    inSpace: 'SpaceModel',
    space: 'SpaceModel',
    content: 'ContentModel',
    context: 'ProjectContextModel',
    phase: ProjectPhaseModel,
  };

  @observable inSpace = null;
  @observable space = null;
  @observable slug = '';
  @observable name = '';
  @observable description = '';
  @observable descriptionSnippet = '';
  @observable image = '';
  @observable private = false;
  @observable starCount = 0;
  @observable isStarred = false;
  @observable forkCount = 0;
  @observable sourceForkName = '';
  @observable contributionCount = 0;
  @observable lastUpdated = '';
  @observable collections = null;
  @observable creatorProfile = null;
  @observable parentContent = null;
  @observable content = null;
  @observable phase = null;
  @observable license = '';
  @observable tags = [];
  @observable contributions = null;
  @observable contributors = 0;
  @observable hasOpenContribution = false;
  @observable conflictsParent = '';
  @observable snippet = '';
  @observable context = null;
  @observable featuredIn = '';
  @observable forkedFrom = null;
  @observable importStatus = null;
  @observable whitelabel = null;
  @observable filesInfo = null;

  @computed get spacePath() {
    // TODO: make this generic for sub-space scenarios?
    if (!(this.space && this.space.content)) {
      return '';
    }
    let prefix;
    if (this.space.spaceType === 'PROFILE') {
      prefix = '@';
    } else if (this.space.spaceType === 'INITIATIVE') {
      prefix = '+';
    }
    return prefix + this.space.content.slug;
  }

  @computed get path() {
    if (!(this.spacePath && this.slug)) return '';
    return [this.spacePath, this.slug].join('/');
  }

  @computed get imageFallbackPath() {
    const projectNo = parseInt(atob(this.id).split(':')[1], 10);
    if (projectNo % 3 === 0) {
      return 'assets/avatars/fallback/project-fb.svg';
    }
    if (projectNo % 3 === 1) {
      return 'assets/avatars/fallback/project-fb2.svg';
    }
    if (projectNo % 3 === 2) {
      return 'assets/avatars/fallback/project-fb3.svg';
    }
    return 'assets/avatars/fallback/project-fb.svg';
  }
}
ModelRegistry.add(ProjectModel);

export class ProjectApp extends App {
  static registryName = 'ProjectApp';
  static AppProps = {
    tags: TagsApp,
    image: FileApp,
    creatorProfile: 'ProfileApp',
    parentContent: 'ContentApp',
    inSpace: 'SpaceApp',
    space: 'SpaceApp',
    content: 'ContentApp',
    context: 'ProjectContextApp',
    phase: ProjectPhaseApp,
  };

  static Model = ProjectModel;

  @observable editing = false;
  @observable saving = false;
  @observable deleting = false;
  @observable performingDelete = false;

  @observable showingLicenseDetails = true;
  @observable showingFilesProportion = true;

  @computed get isImporting() {
    return (
      !!this.importStatus &&
      this.importStatus.status !== 'finished_successfully'
    );
  }

  @computed get isForked() {
    return !!this.forkedFrom;
  }

  @computed get forkDistance() {
    if (!this.isForked) return null;
    const { contributionsAhead, contributionsBehind } = this.forkedFrom;
    return { contributionsAhead, contributionsBehind };
  }

  @computed get forkOf() {
    if (!this.isForked) return null;
    const { project } = this.forkedFrom;
    return new ProjectApp(null, project);
  }

  @computed get route() {
    if (!(this.space && this.space.content && this.slug)) return {};
    const spaceType = this.space.spaceType.toLowerCase();
    const spaceSymbol = spaceType === 'initiative' ? '+' : '@';
    return {
      name: `${spaceType}.project`,
      params: {
        slug: this.slug,
        [spaceType]: `${spaceSymbol}${this.space.content.slug}`,
      },
    };
  }

  @action SetEditing = editing => {
    this.editing = editing;
  };

  @action SetSaving = saving => {
    this.saving = saving;
  };

  @action SetDeleting = deleting => {
    this.deleting = deleting;
  };

  @action SetPerformingDelete = performingDelete => {
    this.performingDelete = performingDelete;
  };

  @action SetDeleted = deleted => {
    this.deleted = deleted;
  };

  @action AcceptInvitation = invitation => {
    const options = {
      onSuccess: () => {
        clientStore.RemoveInvitation(invitation);
        Alert.success(
          'The invitation has been accepted. Welcome to the project!',
        );
      },
      onUserErr: invitation.onError,
      onSysErr: invitation.onError,
    };
    invitation.Accept(options);
  };

  @action RejectInvitation = invitation => {
    const options = {
      onSuccess: () => {
        clientStore.RemoveInvitation(invitation);
        Alert.success('The invitation has been rejected');
      },
      onUserErr: invitation.onError,
      onSysErr: invitation.onError,
    };
    invitation.Reject(options);
  };

  @action Edit = () => {
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.editFormStore = new FormStore(this, conf, 'editProject');
    this.editing = true;
  };

  @action EditTags = () => {
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.editTagsFormStore = new FormStore(this, tagsConf, 'editProjectTags');
  };

  @action DoneEditingTags = () => {
    modalStore.ClearComponent();
    this.editTagsFormStore = null;
  };

  @action Cancel = () => {
    this.ResetLocals();
    this.editFormStore = null;
    this.editing = false;
  };

  @action EditLicense = () => {
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.editLicenseFormStore = new FormStore(this, conf, 'editLicense');
  };

  @action SelectLicense = () => {
    modalStore.ClearComponent();
  };

  @action EditPhase = () => {
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.editPhaseFormStore = new FormStore(this, conf, 'editPhase');
  };

  @action SelectPhase = () => {
    modalStore.ClearComponent();
  };

  @action ToggleLicenseDetailsView = () => {
    this.showingLicenseDetails = !this.showingLicenseDetails;
  };

  @action ToggleFilesProportionDetailsView = () => {
    this.showingFilesProportion = !this.showingFilesProportion;
  };

  @computed get licenseLimits() {
    if (
      !this.license ||
      !Object.keys(LicensesInfo).includes(
        this.license.abreviation?.toLowerCase(),
      )
    ) {
      return LicensesInfo.unlicensed;
    }
    return LicensesInfo[this.license.abreviation.toLowerCase()];
  }

  @computed get fileDetails() {
    return this.filesInfo;
  }
}

AppRegistry.add(ProjectApp);

export class ProjectArrayApp extends ArrayApp {
  static registryName = 'ProjectArrayApp';
  static ElementApp = ProjectApp;
  cardLayout = new ThreeColumnLayout();
}
AppRegistry.add(ProjectArrayApp);
